import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetAdminSettingsQuery, useUpdateAdminSettingsMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { showSuccess, showError } from '../../../utils/toast';
import ImageUpload from '../../../components/ImageUpload';

const AdminSettings: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetAdminSettingsQuery(undefined);
  const [updateSettings, { isLoading: isUpdating }] = useUpdateAdminSettingsMutation();
  
  const [formData, setFormData] = useState({
    show_activity: '1',
    show_article: '1',
    show_exhibition: '1',
    payment_mode: 'test',
    app_version: {
      android: '',
      ios: '',
      title: '',
      title_ar: '',
      message: '',
      message_ar: '',
    },
    fesilah_market: {
      title: '',
      description: '',
      image: '',
    },
    fesilah_education: {
      title: '',
      description: '',
      image: '',
    },
  });

  const [marketImageFile, setMarketImageFile] = useState<File | null>(null);
  const [educationImageFile, setEducationImageFile] = useState<File | null>(null);
  const [existingMarketImage, setExistingMarketImage] = useState<string | undefined>(undefined);
  const [existingEducationImage, setExistingEducationImage] = useState<string | undefined>(undefined);

  const [activeTab, setActiveTab] = useState<'general' | 'market' | 'education' | 'payment'>('general');

  useEffect(() => {
    if (data?.data) {
      const settings = data.data;

      const marketImageRaw = settings.fesilah_market?.image || '';
      const educationImageRaw = settings.fesilah_education?.image || '';

      const extractFilename = (value: string): string | undefined => {
        if (!value) {
          return undefined;
        }
        if (value.includes('/uploads/')) {
          const parts = value.split('/uploads/');
          return parts[1] || undefined;
        }
        return value;
      };

      setFormData({
        show_activity: String(settings.show_activity || 1),
        show_article: String(settings.show_article || 1),
        show_exhibition: String(settings.show_exhibition || 1),
        payment_mode: settings.payment_mode || 'test',
        app_version: {
          android: settings.app_version?.android || '',
          ios: settings.app_version?.ios || '',
          title: settings.app_version?.title || '',
          title_ar: settings.app_version?.title_ar || '',
          message: settings.app_version?.message || '',
          message_ar: settings.app_version?.message_ar || '',
        },
        fesilah_market: {
          title: settings.fesilah_market?.title || '',
          description: settings.fesilah_market?.description || '',
          image: marketImageRaw || '',
        },
        fesilah_education: {
          title: settings.fesilah_education?.title || '',
          description: settings.fesilah_education?.description || '',
          image: educationImageRaw || '',
        },
      });

      setExistingMarketImage(extractFilename(marketImageRaw));
      setExistingEducationImage(extractFilename(educationImageRaw));
      setMarketImageFile(null);
      setEducationImageFile(null);
    }
  }, [data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('app_version.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        app_version: {
          ...prev.app_version,
          [field]: value,
        },
      }));
      return;
    }

    if (name.startsWith('fesilah_market.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        fesilah_market: {
          ...prev.fesilah_market,
          [field]: value,
        },
      }));
      return;
    }

    if (name.startsWith('fesilah_education.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        fesilah_education: {
          ...prev.fesilah_education,
          [field]: value,
        },
      }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMarketImageChange = (file: File | null) => {
    setMarketImageFile(file);
  };

  const handleEducationImageChange = (file: File | null) => {
    setEducationImageFile(file);
  };

  const handleRemoveExistingMarketImage = () => {
    setExistingMarketImage(undefined);
  };

  const handleRemoveExistingEducationImage = () => {
    setExistingEducationImage(undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const submitData = new FormData();

      submitData.append('show_activity', String(parseInt(formData.show_activity) || 1));
      submitData.append('show_article', String(parseInt(formData.show_article) || 1));
      submitData.append('show_exhibition', String(parseInt(formData.show_exhibition) || 1));
      submitData.append('payment_mode', formData.payment_mode);

      submitData.append('app_version[android]', formData.app_version.android);
      submitData.append('app_version[ios]', formData.app_version.ios);
      submitData.append('app_version[title]', formData.app_version.title);
      submitData.append('app_version[title_ar]', formData.app_version.title_ar);
      submitData.append('app_version[message]', formData.app_version.message);
      submitData.append('app_version[message_ar]', formData.app_version.message_ar);

      submitData.append('fesilah_market[title]', formData.fesilah_market.title);
      submitData.append('fesilah_market[description]', formData.fesilah_market.description || '');

      if (existingMarketImage && !marketImageFile) {
        submitData.append('fesilah_market[image]', existingMarketImage);
      }

      if (marketImageFile) {
        submitData.append('fesilah_market_image', marketImageFile);
      }

      submitData.append('fesilah_education[title]', formData.fesilah_education.title);
      submitData.append('fesilah_education[description]', formData.fesilah_education.description || '');

      if (existingEducationImage && !educationImageFile) {
        submitData.append('fesilah_education[image]', existingEducationImage);
      }

      if (educationImageFile) {
        submitData.append('fesilah_education_image', educationImageFile);
      }

      await updateSettings(submitData).unwrap();
      showSuccess('Settings updated successfully!');
    } catch (err: any) {
      showError(err?.data?.message || 'Update failed');
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                Settings
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">settings</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <ul className="nav nav-tabs nav-line-tabs mb-5 fs-6">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
                      onClick={() => setActiveTab('general')}
                    >
                      General
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === 'market' ? 'active' : ''}`}
                      onClick={() => setActiveTab('market')}
                    >
                      Fesilah Market
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === 'education' ? 'active' : ''}`}
                      onClick={() => setActiveTab('education')}
                    >
                      Fesilah Education
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === 'payment' ? 'active' : ''}`}
                      onClick={() => setActiveTab('payment')}
                    >
                      Payment
                    </button>
                  </li>
                </ul>
                <form 
                  id="kt_modal_new_target_form" 
                  className="form fv-plugins-bootstrap5 fv-plugins-framework" 
                  onSubmit={handleSubmit}
                >
                  {activeTab === 'general' && (
                    <div className="row g-9 mb-8">
                      <div className="col-md-12">
                        <div className="row">
                        <div className="col-md-6 fv-row fv-plugins-icon-container">
                          <label className="required fs-6 fw-semibold mb-2">Activities</label>
                          <select 
                            name="show_activity" 
                            className="form-control mb-5"
                            value={formData.show_activity}
                            onChange={handleInputChange}
                          >
                            <option value="1">Show</option>
                            <option value="0">Hide</option>
                          </select>
                        </div>
                        <div className="col-md-6 fv-row fv-plugins-icon-container">
                          <label className="required fs-6 fw-semibold mb-2">Articles</label>
                          <select 
                            name="show_article" 
                            className="form-control mb-5"
                            value={formData.show_article}
                            onChange={handleInputChange}
                          >
                            <option value="1">Show</option>
                            <option value="0">Hide</option>
                          </select>
                        </div>
                        <div className="col-md-6 fv-row fv-plugins-icon-container">
                          <label className="required fs-6 fw-semibold mb-2">Exhibitions</label>
                          <select 
                            name="show_exhibition" 
                            className="form-control mb-5"
                            value={formData.show_exhibition}
                            onChange={handleInputChange}
                          >
                            <option value="1">Show</option>
                            <option value="0">Hide</option>
                          </select>
                        </div>
                        <div className="col-md-6">
                        </div>
                        <div className="col-md-6 fv-row fv-plugins-icon-container">
                          <label className="required fs-6 fw-semibold mb-2">Android Version</label>
                          <input 
                            type="text" 
                            name="app_version.android" 
                            className="form-control mb-5" 
                            value={formData.app_version.android}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="col-md-6 fv-row fv-plugins-icon-container">
                          <label className="required fs-6 fw-semibold mb-2">IOS Version</label>
                          <input 
                            type="text" 
                            name="app_version.ios" 
                            className="form-control mb-5" 
                            value={formData.app_version.ios}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="col-md-6 fv-row fv-plugins-icon-container">
                          <label className="required fs-6 fw-semibold mb-2">Version Popup Title</label>
                          <input 
                            type="text" 
                            name="app_version.title" 
                            className="form-control mb-5" 
                            value={formData.app_version.title}
                            onChange={handleInputChange}
                            placeholder="Version Popup Title"
                          />
                        </div>
                        <div className="col-md-6 fv-row fv-plugins-icon-container">
                          <label className="required fs-6 fw-semibold mb-2">Version Title Arabic</label>
                          <input 
                            type="text" 
                            name="app_version.title_ar" 
                            className="form-control mb-5" 
                            value={formData.app_version.title_ar}
                            onChange={handleInputChange}
                            placeholder="Version Popup Title"
                          />
                        </div>
                        <div className="col-md-6 fv-row fv-plugins-icon-container">
                          <label className="required fs-6 fw-semibold mb-2">Popup Message</label>
                          <textarea 
                            name="app_version.message" 
                            className="form-control" 
                            cols={30} 
                            rows={2}
                            value={formData.app_version.message}
                            onChange={handleInputChange}
                          ></textarea>
                        </div>
                        <div className="col-md-6 fv-row fv-plugins-icon-container">
                          <label className="required fs-6 fw-semibold mb-2">Popup Message Arabic</label>
                          <textarea 
                            name="app_version.message_ar" 
                            className="form-control" 
                            cols={30} 
                            rows={2}
                            value={formData.app_version.message_ar}
                            onChange={handleInputChange}
                          ></textarea>
                        </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'market' && (
                    <div className="row g-9 mb-8">
                      <div className="col-md-12">
                        <div className="row">
                          <div className="col-md-6 fv-row fv-plugins-icon-container">
                            <label className="required fs-6 fw-semibold mb-2">Title</label>
                            <input
                              type="text"
                              name="fesilah_market.title"
                              className="form-control mb-5"
                              value={formData.fesilah_market.title}
                              onChange={handleInputChange}
                            />
                          </div>
                          <div className="col-md-6 fv-row fv-plugins-icon-container">
                            <ImageUpload
                              existingImage={existingMarketImage}
                              onImageChange={handleMarketImageChange}
                              onRemoveExisting={handleRemoveExistingMarketImage}
                              label="Image"
                              name="fesilah_market_image"
                            />
                          </div>
                          <div className="col-md-12 fv-row fv-plugins-icon-container">
                            <label className="required fs-6 fw-semibold mb-2">Description</label>
                            <textarea
                              name="fesilah_market.description"
                              className="form-control"
                              cols={30}
                              rows={3}
                              value={formData.fesilah_market.description}
                              onChange={handleInputChange}
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'education' && (
                    <div className="row g-9 mb-8">
                      <div className="col-md-12">
                        <div className="row">
                          <div className="col-md-6 fv-row fv-plugins-icon-container">
                            <label className="required fs-6 fw-semibold mb-2">Title</label>
                            <input
                              type="text"
                              name="fesilah_education.title"
                              className="form-control mb-5"
                              value={formData.fesilah_education.title}
                              onChange={handleInputChange}
                            />
                          </div>
                          <div className="col-md-6 fv-row fv-plugins-icon-container">
                            <ImageUpload
                              existingImage={existingEducationImage}
                              onImageChange={handleEducationImageChange}
                              onRemoveExisting={handleRemoveExistingEducationImage}
                              label="Image"
                              name="fesilah_education_image"
                            />
                          </div>
                          <div className="col-md-12 fv-row fv-plugins-icon-container">
                            <label className="required fs-6 fw-semibold mb-2">Description</label>
                            <textarea
                              name="fesilah_education.description"
                              className="form-control"
                              cols={30}
                              rows={3}
                              value={formData.fesilah_education.description}
                              onChange={handleInputChange}
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'payment' && (
                    <div className="row g-9 mb-8">
                      <div className="col-md-6 fv-row fv-plugins-icon-container">
                        <label className="required fs-6 fw-semibold mb-2">Payment Mode</label>
                        <select
                          name="payment_mode"
                          className="form-control"
                          value={formData.payment_mode}
                          onChange={handleInputChange}
                        >
                          <option value="test">Test</option>
                          <option value="live">Live</option>
                        </select>
                        <div className="mt-3">
                          <span className={`badge ${formData.payment_mode === 'live' ? 'badge-light-success' : 'badge-light-warning'} fs-7`}>
                            {formData.payment_mode === 'live' ? '● Live payments are active' : '● Test mode — no real charges'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="text-left">
                    <button
                      type="submit"
                      id="kt_modal_new_target_submit"
                      className="btn btn-base"
                      disabled={isUpdating}
                    >
                      {isUpdating ? (
                        <span className="indicator-progress">Please wait...
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span></span>
                      ) : (
                        <span className="indicator-label">Update Settings</span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
